import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Theme,
  TablePagination,
  TableSortLabel,
  Box,
  IconButton,
  Modal,
} from "@mui/material";
import { StyledTableRow } from "../../../../../styles/Shipmentstyles";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import { SortType } from "../../../../../models/SortType";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { UnpaidSampleReturnDetails } from "../../../../../models/ProjectManagement/UnpaidSample";
import { formatDateWithoutTime } from "../../../../../views/components/common/functions/formatDate";
import pencil_uderline from "../../../../../assets/pencil_uderline.png";
import { useState } from "react";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CloseIcon from "@mui/icons-material/Close";

interface UnpaidSampleTableProps {
  maxHeight: number;
  data: UnpaidSampleReturnDetails[] | null;
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  sortBy: string;
  sortType: SortType;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string, sortType: SortType) => void;
  minHeight?: number;
  onActionClick: (unpaid_sample_return_details_id: number) => void;
}

type ModalData = {
  type: "remarks" | "address";
  data: UnpaidSampleReturnDetails;
} | null;

const UnpaidSampleTable = ({
  maxHeight,
  data,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  minHeight = window.innerHeight - 350,
  onActionClick,
}: UnpaidSampleTableProps) => {
  const theme = useTheme<Theme>();
  const [modalData, setModalData] = useState<ModalData>(null);

  const handleRowsChange = (value: number) => {
    onPageChange(1);
    onRowsPerPageChange(value);
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  const getModalContent = (): string => {
    if (!modalData) return "No content available";

    if (modalData.type === "remarks") {
      return modalData.data.remarks || "No remarks available";
    }

    if (modalData.type === "address") {
      return (
        [
          modalData.data.address1,
          modalData.data.address2,
          modalData.data.cityName,
          modalData.data.stateName,
          modalData.data.countryName,
          modalData.data.postalCode,
        ]
          .filter(Boolean)
          .join(", ") || "No address available"
      );
    }

    return "No content available";
  };

  return (
    <>
      <TableContainer sx={{ maxHeight: maxHeight, minHeight: minHeight }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ width: "100%" }}>
              <AppTableHeaderCell
                sx={{
                  color: theme.palette.customColors.tableGrayHeadingColor,
                  fontSize: "0.875rem",
                  fontWeight: 700,
                  position: "sticky",
                  left: 0,
                  zIndex: 12,
                  backgroundColor: theme.palette.customColors.white,
                }}
              >
                <TableSortLabel
                  active={sortBy === "order_id"}
                  direction={sortType === SortType.ASC ? "asc" : "desc"}
                  onClick={() =>
                    onSortChange(
                      "order_id",
                      sortBy === "order_id" && sortType === SortType.DESC
                        ? SortType.ASC
                        : SortType.DESC
                    )
                  }
                  IconComponent={() => (
                    <span>{renderSortIcon("order_id")}</span>
                  )}
                  sx={{ fontSize: "0.875rem" }}
                >
                  ORDER ID
                </TableSortLabel>
              </AppTableHeaderCell>

              <AppTableHeaderCell
                sx={{
                  color: theme.palette.customColors.tableGrayHeadingColor,
                  fontSize: "0.875rem",
                  fontWeight: 700,
                }}
                title="PICKUP ID"
              />
              <AppTableHeaderCell
                sx={{
                  color: theme.palette.customColors.tableGrayHeadingColor,
                  fontSize: "0.875rem",
                  fontWeight: 700,
                }}
                title="ORDER STATUS"
              />
              <AppTableHeaderCell
                sx={{
                  color: theme.palette.customColors.tableGrayHeadingColor,
                  fontSize: "0.875rem",
                  fontWeight: 700,
                }}
                title="CLIENT CONTACTED"
              />
              <AppTableHeaderCell
                sx={{
                  color: theme.palette.customColors.tableGrayHeadingColor,
                  fontSize: "0.875rem",
                  fontWeight: 700,
                }}
                title="PICKUP CONFIRMED"
              />
              <AppTableHeaderCell
                sx={{
                  color: theme.palette.customColors.tableGrayHeadingColor,
                  fontSize: "0.875rem",
                  fontWeight: 700,
                }}
                title="EXPECTED PICKUP DATE"
              />
              <AppTableHeaderCell
                sx={{
                  color: theme.palette.customColors.tableGrayHeadingColor,
                  fontSize: "0.875rem",
                  fontWeight: 700,
                }}
                title="RECEIVED AT OFFICE"
              />
              <AppTableHeaderCell
                sx={{
                  color: theme.palette.customColors.tableGrayHeadingColor,
                  fontSize: "0.875rem",
                  fontWeight: 700,
                }}
                title="CREATED BY"
              />
              <AppTableHeaderCell
                sx={{
                  color: theme.palette.customColors.tableGrayHeadingColor,
                  fontSize: "0.875rem",
                  fontWeight: 700,
                }}
              >
                <TableSortLabel
                  active={sortBy === "created_date"}
                  direction={sortType === SortType.ASC ? "asc" : "desc"}
                  onClick={() =>
                    onSortChange(
                      "created_date",
                      sortBy === "created_date" && sortType === SortType.DESC
                        ? SortType.ASC
                        : SortType.DESC
                    )
                  }
                  IconComponent={() => (
                    <span>{renderSortIcon("created_date")}</span>
                  )}
                  sx={{ fontSize: "0.875rem" }}
                >
                  CREATED DATE
                </TableSortLabel>
              </AppTableHeaderCell>
              <AppTableHeaderCell
                sx={{
                  color: theme.palette.customColors.tableGrayHeadingColor,
                  fontSize: "0.875rem",
                  fontWeight: 700,
                }}
              >
                <TableSortLabel
                  active={sortBy === "actual_pickup_date"}
                  direction={sortType === SortType.ASC ? "asc" : "desc"}
                  onClick={() =>
                    onSortChange(
                      "actual_pickup_date",
                      sortBy === "actual_pickup_date" &&
                        sortType === SortType.DESC
                        ? SortType.ASC
                        : SortType.DESC
                    )
                  }
                  IconComponent={() => (
                    <span>{renderSortIcon("actual_pickup_date")}</span>
                  )}
                  sx={{ fontSize: "0.875rem" }}
                >
                  ACTUAL PICKUP DATE
                </TableSortLabel>
              </AppTableHeaderCell>
              <AppTableHeaderCell
                sx={{
                  color: theme.palette.customColors.tableGrayHeadingColor,
                  fontSize: "0.875rem",
                  fontWeight: 700,
                }}
              >
                <TableSortLabel
                  active={sortBy === "actual_pack_received_date"}
                  direction={sortType === SortType.ASC ? "asc" : "desc"}
                  onClick={() =>
                    onSortChange(
                      "actual_pack_received_date",
                      sortBy === "actual_pack_received_date" &&
                        sortType === SortType.DESC
                        ? SortType.ASC
                        : SortType.DESC
                    )
                  }
                  IconComponent={() => (
                    <span>{renderSortIcon("actual_pack_received_date")}</span>
                  )}
                  sx={{ fontSize: "0.875rem" }}
                >
                  PACK RECEIVED DATE
                </TableSortLabel>
              </AppTableHeaderCell>
              <AppTableHeaderCell
                sx={{
                  color: theme.palette.customColors.tableGrayHeadingColor,
                  fontSize: "0.875rem",
                  fontWeight: 700,
                }}
                title="REMARKS"
              />
              <AppTableHeaderCell
                sx={{
                  color: theme.palette.customColors.tableGrayHeadingColor,
                  fontSize: "0.875rem",
                  fontWeight: 700,
                }}
                title="CUSTOMER NAME"
              />
              <AppTableHeaderCell
                sx={{
                  color: theme.palette.customColors.tableGrayHeadingColor,
                  fontSize: "0.875rem",
                  fontWeight: 700,
                }}
                title="CONTACT NO"
              />
              <AppTableHeaderCell
                sx={{
                  color: theme.palette.customColors.tableGrayHeadingColor,
                  fontSize: "0.875rem",
                  fontWeight: 700,
                }}
                title="EMAIL"
              />
              <AppTableHeaderCell
                sx={{
                  color: theme.palette.customColors.tableGrayHeadingColor,
                  fontSize: "0.875rem",
                  fontWeight: 700,
                  width: "200px",
                }}
                title="ADDRESS"
              />

              <AppTableHeaderCell
                sx={{
                  color: theme.palette.customColors.tableGrayHeadingColor,
                  fontSize: "0.875rem",
                  fontWeight: 700,
                  position: "sticky",
                  right: 0,
                  zIndex: 15,
                  backgroundColor: theme.palette.customColors.white,
                }}
                title="RETURN PROCESS"
              />
            </TableRow>
          </TableHead>

          <TableBody>
            {data?.map((data: UnpaidSampleReturnDetails) => (
              <StyledTableRow key={data.order_id}>
                <AppTableRowCell
                  sx={{
                    textAlign: "center",
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    minWidth: "100px",
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                    backgroundColor: theme.palette.customColors.white,
                  }}
                >
                  {data.order_id}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{
                    textAlign: "center",
                    fontSize: "0.8rem",
                    minWidth: "100px",
                  }}
                >
                  {data.unpaid_sample_return_details_id || "-"}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{
                    textAlign: "center",
                    fontSize: "0.8rem",
                    minWidth: "120px",
                  }}
                >
                  {data.order_archive === 1
                    ? "Archived"
                    : data.order_archive === 0
                      ? "Active"
                      : "-"}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{
                    textAlign: "center",
                    fontSize: "0.8rem",
                    minWidth: "100px",
                  }}
                >
                  {data.client_contacted ? "Yes" : "No"}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{ textAlign: "center", fontSize: "0.8rem" }}
                >
                  {data.pickup_confirmed ? "Yes" : "No"}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{ textAlign: "center", fontSize: "0.8rem" }}
                >
                  {data.pickup_date
                    ? formatDateWithoutTime(data.pickup_date)
                    : "-"}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{
                    textAlign: "center",
                    fontSize: "0.8rem",
                    minWidth: "150px",
                  }}
                >
                  {data.sample_received_at_office ? "Yes" : "No"}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{
                    textAlign: "center",
                    fontSize: "0.8rem",
                    minWidth: "150px",
                  }}
                >
                  {data.sales_poc_name || "-"}
                </AppTableRowCell>

                <AppTableRowCell
                  sx={{
                    textAlign: "center",
                    fontSize: "0.8rem",
                    minWidth: "150px",
                  }}
                >
                  {data.created_date
                    ? formatDateWithoutTime(data.created_date)
                    : "-"}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{ textAlign: "center", fontSize: "0.8rem" }}
                >
                  {data.actual_pickup_date
                    ? formatDateWithoutTime(data.actual_pickup_date)
                    : "-"}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{ textAlign: "center", fontSize: "0.8rem" }}
                >
                  {data.actual_pack_received_date
                    ? formatDateWithoutTime(data.actual_pack_received_date)
                    : "-"}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{
                    textAlign: "center",
                    fontSize: "0.8rem",
                    minWidth: "100px",
                  }}
                >
                  {data.remarks ? (
                    <AppButton
                      label="View Desc"
                      variant="transparentPrimary"
                      customStyles={{
                        fontSize: "0.8rem",
                        padding: "4px 8px",
                        minWidth: "auto",
                      }}
                      onClick={() => setModalData({ type: "remarks", data })}
                    />
                  ) : (
                    "-"
                  )}
                </AppTableRowCell>

                <AppTableRowCell
                  sx={{
                    textAlign: "center",
                    fontSize: "0.8rem",
                    minWidth: "150px",
                  }}
                >
                  {data.customer_name || "-"}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{
                    textAlign: "center",
                    fontSize: "0.8rem",
                    minWidth: "150px",
                  }}
                >
                  {data.contactNo || "-"}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{
                    textAlign: "center",
                    fontSize: "0.8rem",
                    minWidth: "150px",
                  }}
                >
                  {data.email || "-"}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{ textAlign: "center", fontSize: "0.8rem" }}
                >
                  {data.address1 ? (
                    <AppButton
                      label="View Address"
                      variant="transparentPrimary"
                      customStyles={{
                        fontSize: "0.8rem",
                        padding: "4px 8px",
                        minWidth: "auto",
                      }}
                      onClick={() => setModalData({ type: "address", data })}
                    />
                  ) : (
                    "-"
                  )}
                </AppTableRowCell>

                <AppTableRowCell
                  sx={{
                    textAlign: "center",
                    fontSize: "0.8rem",
                    position: "sticky",
                    right: 0,
                    zIndex: 12,
                    backgroundColor: theme.palette.customColors.white,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={(event) =>
                      onActionClick?.(data.unpaid_sample_return_details_id)
                    }
                  >
                    <img src={pencil_uderline} alt="pencil_uderline" />
                  </IconButton>
                </AppTableRowCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
        {data?.length === 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              paddingTop: 15,
            }}
          >
            <CustomTypography
              color={theme.palette.customColors.greyTextColor}
              variant="body2"
              fontWeight={700}
            >
              NO data FOUND!
            </CustomTypography>
          </Box>
        )}
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[30, 50, 100, 150, 200]}
        component="div"
        count={total_count}
        rowsPerPage={limit}
        page={page_no - 1}
        onPageChange={(event, page_no) => onPageChange(page_no + 1)}
        onRowsPerPageChange={(event) =>
          handleRowsChange(parseInt(event.target.value, 10))
        }
      />
      <Modal
        open={!!modalData}
        onClose={() => setModalData(null)}
        aria-labelledby="description-modal-title"
        aria-describedby="description-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "40%",
            maxWidth: 800,
            maxHeight: "80vh",
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <CustomTypography
              color={theme.palette.customColors.greyTextColor}
              variant="heading6"
              fontWeight={700}
            >
              {modalData?.type === "remarks" ? "Remarks" : "Address Details"}
            </CustomTypography>
            <IconButton
              onClick={() => setModalData(null)}
              sx={{
                color: theme.palette.grey[500],
                "&:hover": {
                  color: theme.palette.grey[700],
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              backgroundColor: theme.palette.grey[50],
              p: 2,
              borderRadius: 1,
              border: `1px solid ${theme.palette.grey[200]}`,
              maxHeight: "60vh",
              overflow: "auto",
            }}
          >
            <Box
              dangerouslySetInnerHTML={{
                __html: getModalContent(),
              }}
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "14px",
                lineHeight: "1.6",
                "& h1, & h2, & h3, & h4, & h5, & h6": {
                  marginTop: "16px",
                  marginBottom: "8px",
                },
                "& p": {
                  marginBottom: "12px",
                },
                "& ul, & ol": {
                  marginBottom: "12px",
                  paddingLeft: "20px",
                },
                "& li": {
                  marginBottom: "4px",
                },
              }}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default UnpaidSampleTable;
